import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { collections } from '../data/mock';
import useReveal from '../hooks/useReveal';
import { useCopy } from '../context/SiteCopyContext';
import { useSiteMedia } from '../utils/useSiteMedia';
import { isVideoUrl } from '../utils/mediaType';
import { fitToClass } from '../utils/mediaFit';

/**
 * CollectionTileMedia — slideshow-capable hero for a collection card.
 *
 * If the admin has uploaded multiple images/videos, they rotate using the
 * chosen transition + timing. If the slot is empty, falls back to the mock
 * `collection.hero` image so the storefront always renders something.
 */
const CollectionTileMedia = ({ slot, fallbackSrc, fallbackAlt, visible }) => {
  const cfg = useSiteMedia(slot);
  const media = useMemo(
    () => (cfg?.media || []).length > 0
      ? cfg.media
      : [{ url: fallbackSrc, kind: isVideoUrl(fallbackSrc) ? 'video' : 'image', alt: fallbackAlt }],
    [cfg?.media, fallbackSrc, fallbackAlt]
  );
  const total = media.length;
  const transition = cfg?.transition || 'fade';
  const slideMs = cfg?.duration_ms || 5000;
  const transitionMs = cfg?.transition_ms || 900;

  const [active, setActive] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (total <= 1) return undefined;
    const current = media[active];
    const isVid = current?.kind === 'video' || isVideoUrl(current?.url);
    if (isVid) return undefined;
    timerRef.current = window.setTimeout(() => setActive((i) => (i + 1) % total), slideMs);
    return () => window.clearTimeout(timerRef.current);
  }, [active, total, slideMs, media]);

  return (
    <div className="relative aspect-[5/6] overflow-hidden group bg-black">
      {media.map((m, i) => {
        const isActive = i === active;
        const next = (active + 1) % total;
        if (!isActive && i !== next && total > 2) return null;
        const isVid = m.kind === 'video' || isVideoUrl(m.url);
        let stateClass = '';
        let styleExtra = { transition: `opacity ${transitionMs}ms ease, transform ${transitionMs + 400}ms ease` };
        if (transition === 'fade' || transition === 'dissolve') {
          stateClass = isActive ? 'opacity-100' : 'opacity-0';
        } else if (transition === 'slide') {
          stateClass = isActive ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12';
          styleExtra = { transition: `opacity ${transitionMs}ms ease, transform ${transitionMs}ms ease` };
        } else if (transition === 'zoom') {
          stateClass = isActive ? 'opacity-100 scale-[1.06]' : 'opacity-0 scale-100';
          styleExtra = { transition: `opacity ${transitionMs}ms ease, transform ${slideMs}ms ease-out` };
        }
        return (
          <div key={`${m.url}-${i}`} className={`absolute inset-0 ${stateClass}`} style={styleExtra}>
            {isVid ? (
              <video
                src={m.url}
                autoPlay={isActive}
                muted
                loop={total === 1}
                playsInline
                preload="metadata"
                onEnded={() => { if (total > 1 && isActive) setActive((j) => (j + 1) % total); }}
                className={`w-full h-full ${fitToClass(m.fit)}`}
              />
            ) : (
              <img
                src={m.url}
                alt={m.alt || fallbackAlt}
                className={`w-full h-full ${fitToClass(m.fit)} image-zoom transition-transform duration-[1400ms] ${visible ? 'scale-100' : 'scale-[1.08]'}`}
              />
            )}
          </div>
        );
      })}
      {total > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10" data-testid={`${slot}-dots`}>
          {media.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              aria-label={`Slide ${i + 1}`}
              className={`h-1 rounded-full transition-all ${i === active ? 'w-7 bg-[#F5EDDD]' : 'w-1.5 bg-[#F5EDDD]/40 hover:bg-[#F5EDDD]/70'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CollectionRow = ({ c, reverse, copyPrefix, mediaSlot }) => {
  const [ref, visible] = useReveal();
  const dark = c.dark;
  const subtitle = useCopy(`${copyPrefix}.subtitle`, c.subtitle);
  const title = useCopy(`${copyPrefix}.title`, c.name);
  const description = useCopy(`${copyPrefix}.description`, c.description);
  const ctaLabel = useCopy(`${copyPrefix}.cta`, `Explore ${c.name}`);

  return (
    <section
      ref={ref}
      className={`relative py-24 md:py-32 overflow-hidden ${dark ? 'bg-[#1E140B] text-[#F5EDDD]' : 'bg-[#F5EDDD] text-[#33200F]'}`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center ${reverse ? 'lg:flex-row-reverse' : ''}`}>
          {/* Media */}
          <div className={`lg:col-span-7 ${reverse ? 'lg:order-2' : ''}`}>
            <div className="relative">
              <CollectionTileMedia slot={mediaSlot} fallbackSrc={c.hero} fallbackAlt={c.name} visible={visible} />
              <div className={`absolute top-6 left-6 text-[10px] tracking-[0.4em] uppercase z-10 ${dark ? 'text-[#F5EDDD]/80' : 'text-[#F5EDDD] mix-blend-difference'}`}>
                / {c.subtitle}
              </div>
            </div>
          </div>

          {/* Copy */}
          <div className={`lg:col-span-5 ${reverse ? 'lg:order-1' : ''}`}>
            <p className={`text-[11px] tracking-[0.32em] uppercase mb-5 ${dark ? 'text-[#C9B596]' : 'text-[#A07A4F]'}`}>
              {subtitle}
            </p>
            <h2
              className={`text-5xl md:text-6xl lg:text-7xl leading-[1.02] mb-6 ${dark ? 'text-[#F5EDDD]' : 'text-[#33200F]'}`}
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              <span className="italic" style={{ color: dark ? '#E8DBC4' : '#5B3A24' }}>
                {title}.
              </span>
            </h2>
            <p className={`text-[15px] leading-[1.95] mb-9 max-w-md ${dark ? 'text-[#C9B596]' : 'text-[#5B3A24]'}`}>
              {description}
            </p>
            <Link
              to={`/collections/${c.id}`}
              className={`group inline-flex items-center gap-3 text-[12px] tracking-[0.22em] uppercase border-b pb-2 transition-all hover:gap-5 ${
                dark ? 'text-[#F5EDDD] border-[#F5EDDD]' : 'text-[#33200F] border-[#33200F]'
              }`}
            >
              {ctaLabel}
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const CollectionShowcase = () => (
  <>
    <CollectionRow c={collections[0]} reverse={false} copyPrefix="showcase.thread" mediaSlot="home_thread" />
    <CollectionRow c={collections[1]} reverse={true} copyPrefix="showcase.timber" mediaSlot="home_timber" />
  </>
);

export default CollectionShowcase;
